import { useMemo, useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useGenerateInvitedUserOtp } from './useGenerateInvitedUserOtp';
import { useVerifyInvitedUserOtp } from './useVerifyInvitedUserOtp';

interface UseInvitedUserOtpVerificationParams {
  userId: string;
  phoneNumber: string;
}

/**
 * Custom hook for managing OTP verification state and logic for invited users
 */
export const useInvitedUserOtpVerification = ({
  userId,
  phoneNumber,
}: UseInvitedUserOtpVerificationParams): {
  otpGenerated: boolean;
  otpValue: string;
  isVerifying: boolean;
  verificationId: string;
  originalVerifiedPhoneNumber: string;
  isPhoneNumberVerified: boolean;
  resendTimer: number;
  canResendOtp: boolean;
  setOtpValue: (value: string) => void;
  setOriginalVerifiedPhoneNumber: (value: string) => void;
  handleGenerateOtp: () => Promise<void>;
  handleEditPhoneNumber: () => void;
  handleResendOtp: () => Promise<void>;
  handleVerifyOtp: () => Promise<void>;
  resetOtpState: () => void;
  generateOtpMutation: ReturnType<typeof useGenerateInvitedUserOtp>;
  verifyOtpMutation: ReturnType<typeof useVerifyInvitedUserOtp>;
} => {
  const [otpGenerated, setOtpGenerated] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationId, setVerificationId] = useState<string>('');
  const [originalVerifiedPhoneNumber, setOriginalVerifiedPhoneNumber] = useState<string>('');
  const [resendTimer, setResendTimer] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const generateOtpMutation = useGenerateInvitedUserOtp();
  const verifyOtpMutation = useVerifyInvitedUserOtp();

  const isPhoneNumberVerified = useMemo(() => Boolean(originalVerifiedPhoneNumber) && phoneNumber === originalVerifiedPhoneNumber, [originalVerifiedPhoneNumber, phoneNumber]);
  const canResendOtp = resendTimer === 0;

  // Timer effect
  useEffect(() => {
    // Clear any existing timer before starting a new one
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (resendTimer > 0) {
      timerRef.current = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [resendTimer]);

  const handleGenerateOtp = async () => {
    if (!phoneNumber || phoneNumber.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    try {
      const result = await generateOtpMutation.mutateAsync({
        userId,
        verificationType: 'PHONE_NUMBER',
        phoneNumber,
        emailAddress: '',
      });

      // Extract and set verificationId from response
      const verificationIdValue = result?.data?.verificationId;
      if (verificationIdValue) {
        setVerificationId(verificationIdValue);
        setOtpGenerated(true);
        setResendTimer(60); // Start 1-minute timer
      } else if (result?.status) {
        // If status is true but no verificationId, still consider it successful
        setOtpGenerated(true);
        setResendTimer(60); // Start 1-minute timer
      }
    } catch (error) {
      console.error('Error generating OTP:', error);
    }
  };

  const handleEditPhoneNumber = () => {
    setOtpGenerated(false);
    setOtpValue('');
    setVerificationId('');
    setResendTimer(0);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleResendOtp = async () => {
    if (!phoneNumber || phoneNumber.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    try {
      const result = await generateOtpMutation.mutateAsync({
        userId,
        verificationType: 'PHONE_NUMBER',
        phoneNumber,
        emailAddress: '',
      });

      // Extract and set verificationId from response
      const verificationIdValue = result?.data?.verificationId;
      if (verificationIdValue) {
        setVerificationId(verificationIdValue);
      }

      // Reset OTP value when resending
      setOtpValue('');
      setResendTimer(60); // Start 1-minute timer again
    } catch (error) {
      console.error('Error resending OTP:', error);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpValue || otpValue.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    if (!verificationId) {
      toast.error('Verification ID not found. Please generate OTP again.');
      return;
    }

    setIsVerifying(true);
    try {
      const result = await verifyOtpMutation.mutateAsync({
        userId,
        verificationId,
        otp: otpValue,
      });

      // Check if response has message indicating success
      if (result?.status && result?.isPhoneNumberVerified) {
        setOtpGenerated(false);
        setOtpValue('');
        setVerificationId('');
        // Update the original verified phone number to the current number
        setOriginalVerifiedPhoneNumber(phoneNumber);
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
    } finally {
      setIsVerifying(false);
    }
  };

  const resetOtpState = () => {
    setOtpGenerated(false);
    setOtpValue('');
    setVerificationId('');
    setResendTimer(0);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  return {
    // State
    otpGenerated,
    otpValue,
    isVerifying,
    verificationId,
    originalVerifiedPhoneNumber,
    isPhoneNumberVerified,
    resendTimer,
    canResendOtp,
    // Setters (for controlled components)
    setOtpValue,
    setOriginalVerifiedPhoneNumber,
    // Handlers
    handleGenerateOtp,
    handleEditPhoneNumber,
    handleResendOtp,
    handleVerifyOtp,
    resetOtpState,
    // Mutations (for loading states)
    generateOtpMutation,
    verifyOtpMutation,
  };
};
